import { createInstance, dateReviver } from '@/api';
import {
    CreateDeliveryEntityDto,
    CrsDto,
    DeliveryEntityClient,
    DeliveryEntityDetailDto,
    FormatTypeDto,
    UpdateDeliveryEntityDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useDeliveryProcessDefinitionEditStore } from '../delivery-process-definition/delivery-process-definition-edit.store';
import { useDeliveryEntityLinkStore } from '../delivery-entity-link/delivery-entity-link.store';
import { useDeliveryTransformationStore } from '../delivery-transformation/delivery-transformation.store';

export interface DeliveryEntityStoreState {
    items?: { key: string; value: DeliveryEntityDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
    formatTypes: FormatTypeDto[];
    sridTypes: CrsDto[];
}

export const client = new DeliveryEntityClient('', createInstance(), dateReviver);

export const useDeliveryEntityStore = defineStore('configuration-delivery-entity-store', {
    state: (): DeliveryEntityStoreState => ({
        items: [],
        loading: false,
        reloading: false,
        formatTypes: [
            { id: 1, code: 'SHP', name: 'Shapefile' },
            { id: 2, code: 'GML', name: 'Geograhpy Markup Language' },
            { id: 3, code: 'KML', name: 'Keyhole Markup Language' },
            { id: 4, code: 'DXF', name: 'Drawing interchange forma' },
            { id: 5, code: 'CSV', name: 'Comma-separated values' },
            { id: 6, code: 'DBF', name: 'DBase File' }
        ],
        sridTypes: [
            { id: 4258, name: 'Etrs89' },
            { id: 4326, name: 'Wgs84' },
            { id: 31370, name: 'Lambert72' },
            { id: 3857, name: 'WebMercator' },
            { id: 3812, name: 'Lambert2008' }
        ]
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): DeliveryEntityDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        },
        bySpecification: (state) => {
            return (specificationId: number): DeliveryEntityDetailDto[] => {
                const key = `specification-${specificationId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async updateDeliveryEntityUI(items: DeliveryEntityDetailDto[]) {
            items?.forEach((item) => {
                useDeliveryEntityLinkStore().updateDeliveryEntityUI(item);
                useDeliveryTransformationStore().updateDeliveryEntityUI(item);
            });
        },
        async getByDefinition(definitionId: number, reloading: boolean = false) {
            const key = `definition-${definitionId}`;
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateDeliveryEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('deliveryentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async getBySpecification(specificationId: number, reloading: boolean = false) {
            const key = `specification-${specificationId}`;
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                const response = await client.getBySpecification(specificationId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateDeliveryEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('deliveryentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(specificationId: number, dto: CreateDeliveryEntityDto) {
            let hasError = false;
            this.reloading = true;

            try {
                const response = await client.create(dto);

                await this.getBySpecification(specificationId, true);

                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('deliveryentity.detail.error.create.title'), error);
                this.reloading = false;

                return null;
            } finally {
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(specificationId: number, id: number, dto: UpdateDeliveryEntityDto) {
            let hasError = false;
            this.reloading = true;

            try {
                await client.update(id, dto);
                useAlertStore().setSuccess(i18n.global.t('deliveryentity.detail.success.update.title'));
                await this.getBySpecification(specificationId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('deliveryentity.detail.error.update.title'), error);

                this.reloading = false;
            } finally {
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(specificationId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('deliveryentity.detail.success.delete.title'));
                this.getBySpecification(specificationId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('deliveryentity.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(specificationId: number) {
            const key = `specification-${specificationId}`;
            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
